import { useAsyncData, useNuxtData } from '#imports'
import { computed } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import type { GetMerchantOrdersMetadataResponse } from '~/api/seller-orders-api/types'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { OPTION_VALUE_ALL } from '../components/Filters/Filters.constants'
import translations from '../components/Filters/Filters.translations'

function getOptions(values?: { label: string; code: string }[]) {
  return (values || []).map(({ label, code }) => ({
    label,
    value: code,
  }))
}

export function useFiltersOptions() {
  const i18n = useI18n()
  const { activeMarkets } = useSellerInfoStore()
  const $httpFetch = useHttpFetch()

  const { data } = useAsyncData(
    'merchant-orders-metadata',
    () => $httpFetch('/bm/merchants/orders/metadata'),
    {
      dedupe: 'defer',
      getCachedData: (key) =>
        useNuxtData<GetMerchantOrdersMetadataResponse>(key).data.value ||
        undefined,
    },
  )

  const genericOptionAll = {
    label: i18n(translations.all),
    value: OPTION_VALUE_ALL,
  }

  const dateTypeOptions = computed(() => [
    genericOptionAll,
    ...getOptions(data.value?.filterOrderDateType),
  ])

  const missingActionOptions = computed(() => [
    genericOptionAll,
    ...getOptions(data.value?.filterOrderMissingAction),
  ])

  const statesOptions = computed(() => [
    genericOptionAll,
    ...getOptions(data.value?.filterOrderStates),
  ])

  const shippingProvidersOptions = computed(() => {
    const options = (data.value?.carriers || []).map((carrier) => ({
      label: carrier.name,
      value: String(carrier.id),
    }))

    return [genericOptionAll, ...options]
  })

  const availableMarketsOptions = [
    genericOptionAll,
    ...activeMarkets.map((market) => ({
      label: i18n.country(market),
      value: market,
    })),
  ]

  const backshipOptions = [
    {
      label: i18n(translations.withAndWithoutBackShip),
      value: OPTION_VALUE_ALL,
    },
    { label: i18n(translations.withBackShip), value: 'true' },
    { label: i18n(translations.withoutBackShip), value: 'false' },
  ]

  const vatTypeOptions = (data.value?.vatTypes || []).map((vatType) => ({
    label: vatType.label,
    value: vatType.code,
  }))

  return {
    availableMarketsOptions,
    backshipOptions,
    dateTypeOptions,
    missingActionOptions,
    statesOptions,
    shippingProvidersOptions,
    vatTypeOptions,
  }
}
